import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, Trash2, Plus, Activity, AlertTriangle, ShieldCheck, Ban, MailCheck } from "lucide-react";
import AdminShell, { Panel, Btn, StatusPill } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const TABS = [
  { id: "sessions", label: "Active Sessions", icon: Activity },
  { id: "failed", label: "Failed Logins", icon: AlertTriangle },
  { id: "settings", label: "Settings", icon: ShieldCheck },
  { id: "signup", label: "Signup Verification", icon: MailCheck },
  { id: "ip", label: "IP Blocklist", icon: Ban },
];

export default function SecurityControls() {
  const [tab, setTab] = useState("sessions");
  const [sessions, setSessions] = useState([]);
  const [failed, setFailed] = useState([]);
  const [settings, setSettings] = useState(null);
  const [ipRules, setIpRules] = useState({ whitelist: [], blocklist: [] });
  const [signupVerification, setSignupVerification] = useState(null);
  const [signupSaving, setSignupSaving] = useState(false);
  const [signupMsg, setSignupMsg] = useState("");
  const sessionsPager = useAdminPager();
  const failedPager = useAdminPager();
  const [sessionsTotal, setSessionsTotal] = useState(0);

  async function loadSessions() {
    const { data } = await api.get("/api/admin/security/sessions", {
      params: { skip: sessionsPager.skip, limit: sessionsPager.limit },
    });
    const { items, total } = unwrapAdminList(data, "sessions");
    setSessions(items);
    sessionsPager.setTotal(total);
    setSessionsTotal(total);
  }
  async function loadFailed() {
    const { data } = await api.get("/api/admin/security/failed-logins", {
      params: { skip: failedPager.skip, limit: failedPager.limit },
    });
    const { items, total } = unwrapAdminList(data, "logs");
    setFailed(items);
    failedPager.setTotal(total);
  }

  async function loadAll() {
    loadSessions();
    loadFailed();
    api.get("/api/admin/security/settings").then(({ data }) => setSettings(data));
    api.get("/api/admin/security/ip-rules").then(({ data }) => setIpRules(data));
    api.get("/api/admin/settings/signup-verification").then(({ data }) => setSignupVerification(data));
  }
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, [sessionsPager.page]);
  useEffect(() => { loadFailed(); /* eslint-disable-next-line */ }, [failedPager.page]);

  async function terminate(id) { await api.delete(`/api/admin/security/sessions/${id}`); loadAll(); }
  async function terminateAll() {
    if (!window.confirm("Terminate ALL active sessions?")) return;
    for (const s of sessions) await api.delete(`/api/admin/security/sessions/${s.id}`);
    loadAll();
  }
  async function saveSettings() {
    const { data } = await api.put("/api/admin/security/settings", settings);
    setSettings(data);
  }

  async function saveSignupVerification() {
    setSignupSaving(true);
    setSignupMsg("");
    try {
      const { data } = await api.patch("/api/admin/settings/signup-verification", signupVerification);
      setSignupVerification(data);
      setSignupMsg("Saved successfully.");
    } catch {
      setSignupMsg("Failed to save.");
    } finally {
      setSignupSaving(false);
    }
  }

  return (
    <AdminShell title="Security Controls">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Active Sessions" value={sessions.length} icon={Activity} variant="green" />
        <KpiCard label="Failed Logins (24h)" value={failed.length} delta={`${failed.length > 5 ? "+" : ""}${failed.length}`} icon={AlertTriangle} variant="red" />
        <KpiCard label="2FA Enabled" value={settings?.require_2fa_users ? "Required" : "Optional"} icon={ShieldCheck} variant="yellow" />
        <KpiCard label="Blocked IPs" value={ipRules.blocklist.length} icon={Ban} variant="purple" />
      </div>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="security-tab" />
      <div className="mt-5">
        {tab === "sessions" && (
          <Panel>
            <div className="p-4 border-b border-[#1E2845] flex justify-between items-center">
              <h3 className="text-[14px] font-bold">Active Sessions ({sessionsTotal})</h3>
              {sessions.length > 0 && <Btn kind="danger" onClick={terminateAll} data-testid="terminate-all">Terminate All</Btn>}
            </div>
            <DataTable
              testid="sessions-table"
              columns={[
                { key: "user", header: "User", render: (s) => s.user_email },
                { key: "created_at", header: "Created", render: (s) => <span className="text-[#8892A8] text-[11px]">{new Date(s.created_at).toLocaleString()}</span> },
                { key: "ip", header: "IP", render: (s) => <span className="font-mono text-[11px] text-[#5A6582]">{s.ip || "—"}</span> },
                { key: "actions", header: "", align: "right", render: (s) => <button onClick={() => terminate(s.id)} className="text-[#EF4444] text-[11px] hover:underline">Terminate</button> },
              ]}
              rows={sessions}
              rowKey={(s) => s.id}
              empty="No active sessions."
            />
            <AdminPager
              page={sessionsPager.page}
              total={sessionsPager.total}
              pageSize={sessionsPager.pageSize}
              onPage={sessionsPager.setPage}
              testId="sessions-pager"
            />
          </Panel>
        )}

        {tab === "failed" && (
          <Panel>
            <DataTable
              columns={[
                { key: "created_at", header: "When", render: (f) => <span className="text-[#8892A8]">{new Date(f.created_at).toLocaleString()}</span> },
                { key: "user_id", header: "User ID", render: (f) => f.user_id || "—" },
                { key: "event", header: "Event", render: (f) => <StatusPill value={f.event || "failed"} /> },
                { key: "ip", header: "IP", render: (f) => <span className="font-mono text-[11px] text-[#5A6582]">{f.ip || "—"}</span> },
              ]}
              rows={failed}
              rowKey={(f) => f.id}
              empty="No failed logins."
            />
            <AdminPager
              page={failedPager.page}
              total={failedPager.total}
              pageSize={failedPager.pageSize}
              onPage={failedPager.setPage}
              testId="failed-logins-pager"
            />
          </Panel>
        )}

        {tab === "settings" && settings && (
          <Panel className="p-5 space-y-4 max-w-[560px]" data-testid="security-settings">
            <ToggleRow label="Require 2FA for all users" v={settings.require_2fa_users} on={(v) => setSettings({ ...settings, require_2fa_users: v })} />
            <ToggleRow label="Require 2FA for admins" v={settings.require_2fa_admins} on={(v) => setSettings({ ...settings, require_2fa_admins: v })} />
            <ToggleRow label="Rate limiting" v={settings.rate_limit_enabled} on={(v) => setSettings({ ...settings, rate_limit_enabled: v })} />
            <div className="rounded-[8px] border border-[#1E2845] bg-[#0A0F1F] p-3 text-[12px] text-[#8892A8]">
              AuthKey Indian DLT SMS (PE ID + template IDs) is configured under{" "}
              <Link to="/admin/security/sms-dlt" className="text-cm-blue hover:underline font-semibold">
                SMS / DLT
              </Link>
              .
            </div>            <label className="flex items-center justify-between">
              <span className="text-[13px]">Session timeout</span>
              <select value={settings.session_timeout_minutes ?? 60} onChange={(e) => setSettings({ ...settings, session_timeout_minutes: Number(e.target.value) })} className="h-[36px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px]">
                {[15, 30, 60, 120, 240].map((m) => <option key={m} value={m}>{m} minutes</option>)}
              </select>
            </label>
            <Btn onClick={saveSettings} data-testid="save-security"><Save size={14} /> Save</Btn>
          </Panel>
        )}

        {tab === "signup" && (
          <Panel className="p-5 max-w-[560px]" data-testid="signup-verification-settings">
            <div className="mb-5">
              <h3 className="text-[15px] font-bold mb-1">Signup mobile OTP</h3>
              <p className="text-[12px] text-[#8892A8] leading-relaxed">
                Email ownership OTP is <strong>always required</strong> during signup.
                This toggle only controls whether mobile OTP is required.
                Emails and mobile numbers are unique — the same one cannot be used twice.
                <br /><br />
                When <strong>disabled</strong>, users still verify email, then save a mobile
                number without OTP. They can verify the number later from Profile.
              </p>
            </div>
            {signupVerification ? (
              <div className="space-y-4">
                <ToggleRow
                  label="Require mobile OTP during signup"
                  v={signupVerification.otp_verification_required}
                  on={(v) => setSignupVerification({ ...signupVerification, otp_verification_required: v })}
                />
                <div className={`text-[12px] px-4 py-3 rounded-[8px] border ${
                  signupVerification.otp_verification_required
                    ? "bg-green-900/20 border-green-800 text-green-400"
                    : "bg-amber-900/20 border-amber-700 text-amber-400"
                }`}>
                  {signupVerification.otp_verification_required
                    ? "Mobile OTP is ENABLED — users must verify email + phone during signup."
                    : "Mobile OTP is DISABLED — email OTP still required; mobile can be verified later from Profile."}
                </div>
                <div className="flex items-center gap-3">
                  <Btn onClick={saveSignupVerification} disabled={signupSaving} data-testid="save-signup-verification">
                    <Save size={14} /> {signupSaving ? "Saving…" : "Save Changes"}
                  </Btn>
                  {signupMsg && (
                    <span className={`text-[12px] ${signupMsg.includes("success") ? "text-green-400" : "text-red-400"}`}>
                      {signupMsg}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-[13px] text-[#8892A8]">Loading…</div>
            )}
          </Panel>
        )}

        {tab === "ip" && (
          <div className="space-y-3">
            <p className="text-[12px] text-[#8892A8]">
              Per-admin allowed IPs are configured in{" "}
              <a href="/admin/access-control" className="text-[#F0B83A] hover:underline">
                Access Control → Edit Admin
              </a>
              . Use this blocklist for platform-wide banned IPs.
            </p>
            <IpList kind="blocklist" rules={ipRules.blocklist} onChanged={loadAll} />
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function ToggleRow({ label, v, on }) {
  return (
    <label className="flex items-center justify-between cursor-pointer p-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px]">
      <span className="text-[13px] font-semibold">{label}</span>
      <button type="button" onClick={() => on(!v)} className={`w-11 h-6 rounded-full transition relative ${v ? "bg-[#F0B83A]" : "bg-[#1E2845]"}`}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition ${v ? "left-5" : "left-0.5"}`} />
      </button>
    </label>
  );
}

function IpList({ kind, rules, onChanged }) {
  const [ip, setIp] = useState("");
  const [reason, setReason] = useState("");
  async function add() {
    if (!ip) return;
    await api.post(`/api/admin/security/ip-rules/${kind}`, { ip, reason });
    setIp(""); setReason(""); onChanged();
  }
  async function remove(id) {
    await api.delete(`/api/admin/security/ip-rules/${kind}/${id}`);
    onChanged();
  }
  return (
    <Panel className="p-5">
      <h3 className="text-[14px] font-bold mb-3 capitalize">{kind} ({rules.length})</h3>
      <div className="flex gap-2 mb-3">
        <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="IP address" className="flex-1 h-[36px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]" data-testid={`${kind}-ip`} />
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="flex-1 h-[36px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]" />
        <Btn onClick={add} data-testid={`add-${kind}`}><Plus size={14} /></Btn>
      </div>
      <div className="space-y-1.5">
        {rules.length === 0 ? <div className="text-[12px] text-[#8892A8]">No {kind} entries.</div> :
          rules.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-2 bg-[#0A0F1F] rounded-[6px]">
              <div><span className="font-mono text-[12px]">{r.ip}</span> {r.reason && <span className="text-[10px] text-[#8892A8] ml-2">{r.reason}</span>}</div>
              <button onClick={() => remove(r.id)} className="text-[#EF4444] hover:opacity-70"><Trash2 size={12} /></button>
            </div>
          ))}
      </div>
    </Panel>
  );
}
